import { APITypes, StreemApiCallLogEntryCallStatus } from '@streem/api';
import { parsePhoneNumber } from './parse_phone_number';
import { CallLogRow } from '../components/calls/call_log_columns';
import { Theme, IconNames } from '@streem/ui-react';
import { InviteType } from '../types/project.types';

const ACTION_NEEDED = 'Action needed.';
const DISPOSITION_NOT_NEEDED = 'Disposition not submitted';
const NOTE_NOT_SENT = 'Note not sent';
const inviteIcons = {
    onsite: 'HomeIcon' as const,
    phone: 'SMSIcon' as const,
    link: 'LinkIcon' as const,
};

/**
 * Determines if a call log entry has "ended"
 * @param callLogEntry
 */
export const callHasEnded = (callLogEntry: APITypes.StreemApiCallLogEntry): boolean =>
    callLogEntry.callStatus === 'STATUS_CANCELED' ||
    callLogEntry.callStatus === 'STATUS_REJECTED' ||
    callLogEntry.callStatus === 'STATUS_TIMED_OUT' ||
    !!callLogEntry.endTime;

/**
 * Extracts the "toUser" and "fromUser" from a call log entry. Uses the first invitation,
 * then falls back to the onsite check, and returns empty objects if that fails.
 * @param callLogEntry
 */
export const getToFromUsers = (callLogEntry: APITypes.StreemApiCallLogEntry) => {
    const invitations = callLogEntry.invitations || [];
    const callLogRoomInvitation = invitations.length > 0 ? invitations[0] : {};

    let onsiteParticipant;
    if (callLogEntry.onsite && callLogEntry.participants) {
        onsiteParticipant = callLogEntry.participants.find(participant => participant);
    }

    const fromUser = callLogRoomInvitation.fromUser || onsiteParticipant || {};
    const toUser = callLogRoomInvitation.toUser || {};

    const customer = onsiteParticipant ?? fromUser;
    const expert = onsiteParticipant ?? toUser;

    return {
        fromUser,
        toUser,
        customer,
        expert,
    };
};

/**
 * Is there a pending video recording
 * @param callLogEntry
 */
export const hasPendingRecording = (callLogEntry: APITypes.StreemApiCallLogEntry): boolean => {
    const hasRecordingTrack = !!callLogEntry?.artifactCounts?.recordingTrack;
    const hasRecording = !!callLogEntry?.artifactCounts?.recording;

    return hasRecordingTrack && !hasRecording;
};

/**
 * Returns a tuple of the type of invite used to start a Streem call and the title used for alt text for that invite type
 * @param callLogEntry
 */
type InviteTypeTitle = 'Onsite' | 'SMS' | 'Link';

export const getInviteType = (
    callLogEntry: APITypes.StreemApiCallLogEntry,
): [InviteType, InviteTypeTitle] => {
    const { onsite } = callLogEntry;
    if (onsite) {
        return ['onsite', 'Onsite'];
    }
    const { customer } = getToFromUsers(callLogEntry);
    // We're not currently setting the customer phone so checking to see if externalUserId is a phone number
    return typeof customer.phone === 'string' ||
        parsePhoneNumber(customer?.externalUserId)?.parsedPhone
        ? ['phone', 'SMS']
        : ['link', 'Link'];
};

/**
 * Returns a list of call log entries filtered by the reference ID that
 * matches a given integration ID
 * @param integrationId
 * @param callLogEntries
 */
export const filterCallLogEntriesByRefIdMatchingIntegrationId = (
    integrationId: string,
    callLogEntries: APITypes.StreemApiCallLogEntry[],
): APITypes.StreemApiCallLogEntry[] => {
    if (!integrationId) return [];

    /* STREEM-15109 Note: this behavior is a preliminary step to having our users switch from using a refId
     * to using an integrationId until backend support for this feature is implemented.
     * For the time being, these call log entries should be filtered down to those that have an integration ID
     * that matches a reference ID retrieved from the backend. Update after refId sunset grace period has ended.
     */
    return callLogEntries.filter(row => {
        const hasRefIdMatch =
            row.referenceId && row.referenceId.toLowerCase() === integrationId.toLowerCase();
        const hasIntegrationIdMatch =
            row.integrationId && row.integrationId.toLowerCase() === integrationId.toLowerCase();
        return hasRefIdMatch || hasIntegrationIdMatch;
    });
};

/**
 * Returns a list of Call Log Rows that use the room sid as a unique key value (for rendering)
 * @param callLogEntries
 */
export const mapUniqueKeyForCallLogEntries = (
    callLogEntries: APITypes.StreemApiCallLogEntry[],
): CallLogRow[] => {
    return callLogEntries.map(callLogEntry => ({
        key: callLogEntry.roomSid!,
        ...callLogEntry,
    }));
};

/* This functions determines what type of title, and icon to be used based on various call statuses
 * */
export const getIconAvatarDetails = (
    row: CallLogRow,
    customer: APITypes.StreemApiCallLogParticipant,
    theme: typeof Theme,
): { title: string; icon: IconNames; color: string } => {
    if (!customer) {
        return { title: '', icon: 'HelpIcon', color: theme.colors.darkBlue };
    }
    if (row.callStatus === 'STATUS_SUCCESSFUL') {
        const [inviteType, title] = getInviteType(row);
        return { title: title, icon: inviteIcons[inviteType], color: theme.colors.grey90 };
    }
    let title = '';
    if (row.callStatus === 'STATUS_CANCELED') {
        title = 'Missed Call';
    } else if (row.callStatus === 'STATUS_REJECTED') {
        title = 'Call rejected by Expert';
    } else if (row.callStatus === 'STATUS_TIMED_OUT') {
        title = 'Call timed out';
    }
    return { title: title, icon: 'MissedCallIcon', color: theme.colors.red50 };
};

/**
 * Create the error message to show in a call log row, if any.
 * @param {boolean} showDispositionNotSubmitted whether to show the disposition not submitted error
 * @param {boolean} showNoteNotSent whether to show the note not sent error
 * @returns {string | undefined} the compounded error message to show in a call log row, if any
 */
export const getCallLogRowErrorMessage = (
    showDispositionNotSubmitted: boolean,
    showNoteNotSent: boolean,
): string | undefined => {
    let errorMessage: string;

    if (showDispositionNotSubmitted) {
        errorMessage = DISPOSITION_NOT_NEEDED;

        if (showNoteNotSent) {
            errorMessage = `${errorMessage} and ${NOTE_NOT_SENT.toLowerCase()}`;
        }
    } else if (showNoteNotSent) {
        errorMessage = NOTE_NOT_SENT;
    }

    return errorMessage ? `${errorMessage}. ${ACTION_NEEDED}` : errorMessage;
};

export interface CallLogWarningsResult {
    showDispositionNotSubmitted: boolean;
    showNoteNotSent: boolean;
}

export interface CallLogWarningsProps {
    callLogRow: CallLogRow;
    callOutcomeEnabled: boolean;
    sendNoteEnabled: boolean;
}

/**
 * Determine whether to show a warning badge in the call log based on feature flags and call log criteria
 * @param {CallLogWarningsProps} callLogWarningsProps object with the CallLogRow, the showCallOutcomeWarning and showSendNoteWarning feature flags
 * @returns {CallLogWarningsResult} object with fields to indicate whether to show the disposition is not submitted or a note is not sent
 */
export function getCallLogWarnings(
    callLogWarningsProps: CallLogWarningsProps,
): CallLogWarningsResult {
    const { callLogRow, callOutcomeEnabled, sendNoteEnabled } = callLogWarningsProps;
    const { artifactCounts, callStatus, note } = callLogRow;
    const callOutcomeReportNotSubmitted = artifactCounts.roomOutcomeReport === 0;

    /* A note is considered unsent if:
     * 1. The user never saved a note for a successful call
     * 2. The note is finalizable, and it does not have a finalized timestamp set
     * Note: All notes created after the SEND_NOTE_ENABLED feature flags are considered finalizable,
     * so if a note does not exist, assume is_finalizable === true
     */
    const noteNotSent = !note || (note.isFinalizable && note.finalizedAt === undefined);
    const showDispositionNotSubmitted =
        callOutcomeEnabled &&
        callOutcomeReportNotSubmitted &&
        callStatus === StreemApiCallLogEntryCallStatus.SUCCESSFUL;
    const showNoteNotSent =
        sendNoteEnabled && noteNotSent && callStatus === StreemApiCallLogEntryCallStatus.SUCCESSFUL;

    return {
        showNoteNotSent,
        showDispositionNotSubmitted,
    };
}
